/**
 * One "is this thread unread for me" answer, shared by every home surface.
 *
 * Bobbie: home showed 0 unread while Messages showed 2. The staff attention
 * card and the Messages badge only ever counted must-read flags and recent
 * @mentions - an ordinary unread reply, with neither, was invisible to both
 * homes even though the inbox row itself tinted correctly. This hook reuses
 * the INBOX'S OWN unread test (threadUnreadSinceMs, the same function the
 * dot and the per-thread badge already run) so a home count and the inbox
 * can never disagree about which threads are unread.
 */

import { useMemo } from 'react';
import { useAuth } from './auth-context';
import type { ThreadDoc } from './threads';
import { useMessageInboxStates, type MessageInboxStateDoc } from './messageInboxState';
import { unreadThreadCursorsFrom } from './unreadHomeThreadsCore';

export { extraUnreadThreadCount, unreadThreadCursorsFrom, unreadThreadIdsFrom } from './unreadHomeThreadsCore';

export interface UseUnreadHomeThreadIdsResult {
  /** Thread ids unread for the signed-in user, by the inbox's own test. */
  threadIds: Set<string>;
  /** The read cursor each of those threads counts from, keyed by thread id.
   *  Lets a recent @mention count only while it is still unread. */
  unreadSinceByThreadId: Map<string, number>;
  /** Her read and filing cursors, the same map the unread test read, so a
   *  caller that also files other rows (a staff announcement) asks the
   *  same cursors without a second listener. */
  states: Map<string, MessageInboxStateDoc>;
  loading: boolean;
  error: string | null;
}

/**
 * Every thread id unread for the signed-in user right now, derived from
 * `threads`. Deduped by construction (a Set), and safe to union with other
 * "waiting on you" sources - a thread that is also an unacked must-read or
 * a recent mention still counts once here, callers dedupe against those
 * lists by id.
 *
 * Takes `threads` (and its own loading/error) from the CALLER rather than
 * subscribing itself: every current caller already calls useMyThreads() for
 * its own purposes, and a second independent call here would open a second,
 * redundant Firestore listener tree for the identical data. This hook still
 * owns its own useMessageInboxStates(uid) subscription UNLESS a caller
 * already has one to reuse (see FamilyCommandCenter.tsx, which passes its
 * own already-preview-gated states in rather than calling this hook, for
 * exactly that reason).
 *
 * `mutedIds` (the viewer's own notifPrefs.mutedIds) is optional and passed
 * straight through to unreadThreadIdsFrom, so a caller that has already
 * loaded the viewer's user doc can make a fully muted admin board read
 * quiet here too (2026-09-16 morning batch item 2). Omit it and this hook
 * behaves exactly as it did before that fix.
 */
export function useUnreadHomeThreadIds(
  threads: readonly ThreadDoc[],
  threadsLoading: boolean,
  threadsError: string | null,
  mutedIds?: string[] | null
): UseUnreadHomeThreadIdsResult {
  const { user } = useAuth();
  const { states, loading: statesLoading, error: statesError } = useMessageInboxStates(user?.uid);

  const unreadSinceByThreadId = useMemo(
    () => unreadThreadCursorsFrom(threads, states, user?.uid ?? '', mutedIds),
    [threads, states, user, mutedIds]
  );
  const threadIds = useMemo(
    () => new Set(unreadSinceByThreadId.keys()),
    [unreadSinceByThreadId]
  );

  return {
    threadIds,
    unreadSinceByThreadId,
    states,
    loading: threadsLoading || statesLoading,
    error: threadsError || statesError,
  };
}
